import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { NgClass, NgIf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { FileUploaderInterface } from '@vendasta/forms';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { FileControlValueAccessor } from './file-control-value-accessor';
import { FileUploadInfo } from './file-upload-info';
import { FileUploadService } from './file-upload.service';

const STRING_CONTROL_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => VaImageUploaderComponent),
  multi: true,
};

@Component({
  standalone: true,
  imports: [NgClass, TranslateModule, MatButtonModule, MatProgressSpinnerModule, NgIf, MatTooltipModule, MatIconModule],
  selector: 'app-image-uploader',
  styleUrls: ['./va-image-upload.component.scss'],
  template: `
    <div
      #dropBox
      [ngClass]="{ 'image-uploader-container': true, dragover: dragover }"
      (drop)="handleDropEvent($event)"
      (dragover)="dragover = true"
      (dragenter)="dragover = true"
      (dragleave)="dragover = false"
      (dragend)="dragover = false"
      [style.height]="value ? 'auto' : '160px'"
    >
      <div *ngIf="!value && !isUploading" class="prompt content">
        <ng-container *ngIf="!hideNewline; else oneLine">
          <span>{{ 'IMAGE_UPLOAD.DRAG_AND_DROP' | translate }}</span>
          <span class="spacer">{{ 'IMAGE_UPLOAD.OR' | translate }}</span>
        </ng-container>
        <ng-template #oneLine>
          <span>{{ 'IMAGE_UPLOAD.DRAG_AND_DROP' | translate }}&nbsp;{{ 'IMAGE_UPLOAD.OR' | translate }}</span>
        </ng-template>
        <button *ngIf="raisedButton" mat-raised-button color="primary" type="button" (click)="fileInput.click()">
          {{ 'IMAGE_UPLOAD.UPLOAD_IMAGE' | translate }}
        </button>
        <button *ngIf="!raisedButton" mat-button color="primary" type="button" (click)="fileInput.click()">
          {{ 'IMAGE_UPLOAD.UPLOAD_IMAGE' | translate }}
        </button>
        <span class="hint" *ngIf="shouldShowHint">{{ hintText }}</span>
      </div>

      <div *ngIf="isUploading" class="content">
        <mat-progress-spinner mode="indeterminate"></mat-progress-spinner>
      </div>

      <ng-container *ngIf="!hidePreview">
        <div *ngIf="value && !isUploading" class="content preview">
          <img [src]="value" (click)="fileInput.click()" />
        </div>
      </ng-container>
      <ng-container *ngIf="hidePreview">
        <div *ngIf="fileName && !isUploading" class="file-name">
          <a mat-button matTooltip="Replace Image" matTooltipPosition="below" (click)="fileInput.click()">
            {{ fileName }}
          </a>
        </div>
      </ng-container>

      <button class="clear-button" mat-icon-button *ngIf="showClear && value" (click)="clearValue()" type="button">
        <mat-icon>close</mat-icon>
      </button>
      <input #fileInput type="file" (change)="handleInputChange()" class="hide-default-input" [accept]="'image/*'" />
    </div>
    <ng-content selector="[error]"></ng-content>
  `,
  providers: [STRING_CONTROL_ACCESSOR],
})
export class VaImageUploaderComponent extends FileControlValueAccessor<string> implements OnInit {
  @Input() uploadUrl: string;
  @Input() showClear = true;
  @Input() maxFileSize: number; // In bytes
  @Input() showMaxFileSize = true;
  @Input() okFileTypes: string[] = [];
  @Input() showOkFileTypes = false;
  @Input() hidePreview = false;
  @Input() customUploader: FileUploaderInterface;
  @Input() hideNewline = false;
  @ViewChild('fileInput', { static: true }) fileInput: ElementRef;
  @ViewChild('dropBox', { static: true }) dropBox: ElementRef;
  @Output() loading = new EventEmitter<boolean>();

  dragover = false;
  isUploading = false;
  @Input() fileName: string;
  @Input() raisedButton = true;

  shouldShowHint: boolean;
  hintText: string;

  computeHintText(): string {
    const hintTexts: string[] = [];
    if (this.maxFileSize && this.showMaxFileSize) {
      hintTexts.push(this.maxFileSizeMb());
    }
    if (this.okFileTypes && this.showOkFileTypes) {
      hintTexts.push(this.okFileTypesString());
    }
    return hintTexts.join(' ');
  }

  maxFileSizeMb(): string {
    return `Maximum file size ${(this.maxFileSize / 1024 / 1024).toFixed(2)} MB.`;
  }

  okFileTypesString(): string {
    return `Supported file types: ${this.okFileTypes.join(', ')}.`;
  }

  constructor(
    private fileUploadService: FileUploadService,
    private snackbarService: SnackbarService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
    this.innerValue = '';
  }

  ngOnInit(): void {
    this.shouldShowHint = (this.maxFileSize && this.showMaxFileSize) || (this.okFileTypes && this.showOkFileTypes);
    this.hintText = this.computeHintText();

    ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].map((on) => {
      this.dropBox.nativeElement.addEventListener(on, (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
    });
  }

  clearValue(): void {
    this.value = null;
    this.fileName = null;
  }

  handleFileUploaded(info: FileUploadInfo): void {
    this.value = info.url;
    this.fileName = info.name;
  }

  uploadFiles(file: File): void {
    if (!file) {
      return;
    }
    if (!file.type.match('image/.*')) {
      this.snackbarService.errorSnack(`Unsupported file type ${file.type}, please try a different file.`);
      return;
    }

    if (this.maxFileSize && file.size > this.maxFileSize) {
      this.snackbarService.errorSnack(`File is too large, please try a different file.`);
      return;
    }

    const invalidChars = [];
    file.name?.split('').some(function (char): void {
      if (char.charCodeAt(0) > 127) {
        invalidChars.push(char);
      }
    });

    if (invalidChars.length > 0) {
      let errorStr = 'File name cannot contain character(s) ';
      for (const char of invalidChars) {
        errorStr += char + ', ';
      }
      this.snackbarService.openErrorSnack(errorStr.substring(0, errorStr.length - 2));
      return;
    }

    this.isUploading = true;
    this.loading.emit(true);
    let upload$;
    if (this.customUploader) {
      upload$ = this.customUploader.uploadFile(file);
    } else {
      upload$ = this.fileUploadService.upload(this.uploadUrl, file);
    }
    upload$.subscribe(
      (fileUpload) => {
        this.isUploading = false;
        this.loading.emit(false);
        this.handleFileUploaded(fileUpload);
        this.changeDetectorRef.detectChanges();
      },
      (err) => {
        console.log(err);
        this.isUploading = false;
        this.loading.emit(false);
        this.snackbarService.errorSnack('Unable to upload file. Please try again.');
        this.changeDetectorRef.detectChanges();
      },
    );
  }

  handleInputChange(): void {
    this.uploadFiles(this.fileInput.nativeElement.files[0]);
  }

  handleDropEvent(event: DragEvent): void {
    this.dragover = false;
    if (event.dataTransfer) {
      this.uploadFiles(event.dataTransfer.files[0]);
    } else {
      this.snackbarService.errorSnack(`Your browser does not support drag and drop. Select 'Upload Image' instead.`);
    }
  }
}
